import {
  CANDIDATE_APPLY_PAGE_PATH,
  JOB_PATH,
  MESSAGES_PATH,
} from "./Constants/Paths";
import { encodeBase64URL, toUrlSafeString } from "./helperFunctions";
import { JobCandidateFormSource } from "./Constants/enums";
import { encryptData } from "./cryptoFunctions";
import { logUserAction } from "./analyticsFunctions";
import UserActions from "./Constants/UserActions";

export const APPLICATION_CONTINUATION_PARAM = "resume";
export const APPLICATION_CONTINUATION_VALUE = "next-incomplete";

export const isApplicationContinuationPending = (search) =>
  new URLSearchParams(search).get(APPLICATION_CONTINUATION_PARAM) ===
  APPLICATION_CONTINUATION_VALUE;

export const shouldWaitForApplicationData = ({
  hasCachedJob,
  hasInitialCandidate,
  search,
}) =>
  !hasCachedJob ||
  !hasInitialCandidate ||
  isApplicationContinuationPending(search);

export const buildJobCandidateFormPath = (
  job,
  selectedContactPerson = {},
  { continueApplication = false } = {},
) => {
  const { jobId, contactPerson = null } = job;

  if (jobId && (contactPerson || selectedContactPerson?.userId)) {
    const cp = selectedContactPerson;
    const cpUserId = cp?.userId ?? contactPerson;

    let contactPersonData = {};

    if (cpUserId) {
      contactPersonData = {
        userId: cpUserId,
        workplaceId: cp?.workplaceId ?? "",
        firstName: cp?.firstName ?? "",
        lastName: cp?.lastName ?? "",
        profileImageUrl: cp?.profileImageUrl ?? "",
        workplaceName: cp?.workplaceName ?? "",
        workplaceLogoUrl: cp?.workplaceLogoUrl ?? "",
      };
    }

    const map = {
      job: jobId,
      purpose: "job_candidate_form",
      contactPersonId: cpUserId,
      ...contactPersonData,
    };

    const encodedString = encodeBase64URL(JSON.stringify(map));
    const params = new URLSearchParams({
      preview: encodedString,
      source: `${JobCandidateFormSource.internal}`,
    });
    if (continueApplication) {
      params.set(
        APPLICATION_CONTINUATION_PARAM,
        APPLICATION_CONTINUATION_VALUE,
      );
    }

    return `${CANDIDATE_APPLY_PAGE_PATH}?${params.toString()}`;
  }

  return null;
};

export const navigateToJob = async (job, encryptionConfig) => {
  const { jobId, profession, healthcareWorkplaceDetails, workplaceDetails } =
    job;

  let jobPath;
  if (encryptionConfig != null) {
    jobPath = (await encryptData({ jobId: jobId }, encryptionConfig)) ?? jobId;
  }

  if (jobPath) {
    const workplace = healthcareWorkplaceDetails ?? workplaceDetails;
    const jobCity = (
      workplace.city && workplace.city.length > 0
        ? workplace.city
        : (workplace?.municipality ?? "")
    ).trim();

    let jobTitle = `${profession} till ${jobCity}`;

    const encodeURI = encodeURIComponent(
      `${toUrlSafeString(jobTitle)}_${jobPath}`,
    );

    jobPath = `${JOB_PATH}/${encodeURI}`;
    window.open(jobPath, "_blank");

    logUserAction(UserActions.open_job, { analyticsPayload: { jobId } });

    return true;
  }
  return false;
};

export const navigateToJobWithShareableId = (jobData) => {
  if (!jobData) return false;

  const {
    jobId,
    profession,
    healthcareWorkplaceDetails,
    workplaceDetails,
    shareableJobId,
  } = jobData;

  let jobPath;

  if (shareableJobId) {
    const workplace = healthcareWorkplaceDetails ?? workplaceDetails;
    const jobCity = (
      workplace.city && workplace.city.length > 0
        ? workplace.city
        : (workplace?.municipality ?? "")
    ).trim();

    let jobTitle = `${profession} till ${jobCity}`;

    const encodeURI = encodeURIComponent(
      `${toUrlSafeString(jobTitle)}-id-${shareableJobId}`,
    );

    jobPath = `${JOB_PATH}/${encodeURI}`;
    window.open(jobPath, "_blank");

    logUserAction(UserActions.open_job, { analyticsPayload: { jobId } });

    return true;
  }
  return false;
};

export const navigateToMiniJobWithShareableId = (miniJobDetails) => {
  const { jobId, profession, shareableJobId } = miniJobDetails;

  let jobPath;

  if (shareableJobId) {
    const jobCity = (
      miniJobDetails.workplaceCity && miniJobDetails.workplaceCity.length > 0
        ? miniJobDetails.workplaceCity
        : (miniJobDetails?.workplaceMunicipality ?? "")
    ).trim();
    const jobTitle = `${profession} till ${jobCity}`;

    const encodeURI = encodeURIComponent(
      `${toUrlSafeString(jobTitle)}-id-${shareableJobId}`,
    );

    jobPath = `${JOB_PATH}/${encodeURI}`;
    window.open(jobPath, "_blank");

    logUserAction(UserActions.open_job, { analyticsPayload: { jobId } });

    return true;
  }
  return false;
};

export const navigateToChat = async (chat, encryptionConfig, newTab = true) => {
  const { id } = chat;

  let chatPath;

  if (encryptionConfig != null) {
    chatPath = (await encryptData({ chatId: id }, encryptionConfig)) ?? id;
  }

  if (chatPath) {
    const encodeURI = encodeURIComponent(chatPath);
    chatPath = `${MESSAGES_PATH}/${encodeURI}`;

    if (!newTab) {
      window.location.href = chatPath;
    } else {
      window.open(chatPath, "_blank");
    }

    return true;
  }
  return false;
};
